import React, { FC, useMemo, useState } from 'react';
import { Box, CircularProgress, Container, Dialog, DialogContent, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useGetResourceQuery, useGetResourceEventsQuery } from '../healthWalletSlice';
import { EventTimeline } from '../EventTimeline';
import { DocHistoryError } from './DocHistoryError';
import { formatDate } from '../healthWalletConstants';
import type { GroupedSummary } from '../healthWalletConstants';
import type { SectionProps } from './TabRenderer';
import { allergyCategoryKey, allergyMergeKey, isAllergyAdminRecord, statusChip } from './classify';
import type { AllergyCategoryKey } from './classify';
import { V1_ACCENT, V1_ACCENT_TINT, V1_ACCENT_BORDER } from './theme';
import { V1SearchField } from './V1SearchField';

// ============================================================================
// Allergies tab sections (design: Polygon Health Trends Redesign/Allergies
// Desktop.dc.html, in the DESIGN palette). Data = get_patient_resource
// ('allergies') groups + category/latestText(reaction). The design's LOGICAL
// DEDUP: we merge CASE-VARIANT names ("CHLORHEXIDINE GLUCONATE" + "Chlorhexidine
// Gluconate" → one card; the dialog reads events for up to 3 variant names);
// brand↔generic rollup (Dilaudid↔Hydromorphone) = the deferred RxNorm item.
// Admin/status entries ("Patient not asked") render as the design's FOOTNOTE
// sentence, not cards. Dialog lives HERE (merged shape ≠ shared contract).
// ============================================================================

const PAGE_WIDTH_SX = { maxWidth: { xs: '100%', sm: '94%' } };

interface MergedAllergy {
  name: string;                 // display variant (prefers mixed-case)
  variants: GroupedSummary[];   // per-name groups, count desc — events fetch per variant name
  count: number;
  firstDate: string;
  lastDate: string;
  status?: string | null;
  category?: string | null;
  reaction?: string | null;
}

const useAllergyGroups = () => {
  const { data, isLoading } = useGetResourceQuery('allergies');
  return useMemo(() => {
    const named: GroupedSummary[] = data?.named ?? [];
    const admin = named.filter(isAllergyAdminRecord);
    const real = named.filter((g) => !isAllergyAdminRecord(g));
    // Case-insensitive merge (the design's dedup, minus the RxNorm brand rollup — deferred).
    const byKey = new Map<string, GroupedSummary[]>();
    real.forEach((g) => {
      const k = allergyMergeKey(g); // SHARED key — keeps the pill count (mergedAllergyCount) in lockstep
      byKey.set(k, [...(byKey.get(k) ?? []), g]);
    });
    const merged: MergedAllergy[] = [...byKey.values()].map((variants) => {
      const sorted = [...variants].sort((a, b) => b.count - a.count);
      const display = sorted.find((v) => /[a-z]/.test(v.name ?? '')) ?? sorted[0]; // prefer mixed-case
      return {
        name: display.name,
        variants: sorted,
        count: sorted.reduce((a, v) => a + v.count, 0),
        firstDate: sorted.map((v) => v.firstDate).filter(Boolean).sort()[0] ?? '',
        lastDate: sorted.map((v) => v.lastDate).filter(Boolean).sort().slice(-1)[0] ?? '',
        status: sorted.map((v) => v.status).find(Boolean) ?? null,
        category: sorted.map((v) => v.category).find(Boolean) ?? null,
        reaction: sorted.map((v) => v.latestText).find(Boolean) ?? null,
      };
    }).sort((a, b) => b.count - a.count);
    return { merged, admin, totalRecords: data?.totalRecords ?? 0, isLoading };
  }, [data, isLoading]);
};

// SINGLE source of truth for the tab's categories — hero tiles, filter pills, and sections all iterate THIS
// list (present-only). `pill` = pill text + hero tile label; `title` = section header; `cardLabel` = per-card
// category line; `dot` = section dot; `tileBg/tileFg` = hero tile palette; `empty` = section empty-state copy.
const ALLERGY_CATS: Array<{ key: AllergyCategoryKey; pill: string; title: string; cardLabel: string; dot: string; tileBg: string; tileFg: string; empty: string }> = [
  { key: 'medication', pill: 'Medication', title: 'Medication allergies', cardLabel: 'Medication allergy', dot: V1_ACCENT, tileBg: V1_ACCENT_TINT, tileFg: V1_ACCENT, empty: 'No medication allergies have been documented.' },
  { key: 'food',       pill: 'Food',       title: 'Food allergies',       cardLabel: 'Food allergy',       dot: '#34a760', tileBg: '#e6f6ec',     tileFg: '#1a7f3c', empty: 'No food allergies are currently recorded.' },
  { key: 'other',      pill: 'Other',      title: 'Other allergies',      cardLabel: 'Other allergy',      dot: '#b0b0b6', tileBg: '#f0f0f3',     tileFg: '#48484a', empty: 'No other allergies have been documented.' },
];
const catMeta = (key: AllergyCategoryKey) => ALLERGY_CATS.find((c) => c.key === key)!;
const mergedCategory = (a: MergedAllergy): AllergyCategoryKey =>
  allergyCategoryKey({ category: a.category } as GroupedSummary);

// --- 1. Hero -----------------------------------------------------------------
export const AllergiesHero: FC<SectionProps> = () => {
  const { merged, isLoading } = useAllergyGroups();
  if (isLoading) {
    return <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2, display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Container>;
  }
  const counts: Record<AllergyCategoryKey, number> = { medication: 0, food: 0, other: 0 };
  merged.forEach((a) => { counts[mergedCategory(a)] += 1; });
  const tile = (value: number, label: string, bg: string, fg: string) => (
    <Box sx={{ width: { xs: 92, sm: 104 }, flexShrink: 0, bgcolor: bg, borderRadius: '15px', p: '16px 10px', textAlign: 'center' }}>
      <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: fg, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: fg, mt: 0.75, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
      <Paper elevation={0} sx={{ borderRadius: '20px', p: '24px 26px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: V1_ACCENT, textTransform: 'uppercase', mb: 1.25 }}>
            Your record at a glance
          </Typography>
          <Typography sx={{ fontSize: 21, fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.2px', maxWidth: 580 }}>
            Your record documents <strong>{merged.length}</strong> {merged.length === 1 ? 'allergy' : 'allergies'}.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {ALLERGY_CATS.filter((c) => counts[c.key] > 0).map((c) => (
            <React.Fragment key={c.key}>{tile(counts[c.key], c.pill, c.tileBg, c.tileFg)}</React.Fragment>
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

// --- 2. Explainer -------------------------------------------------------------
export const AllergiesExplainer: FC<SectionProps> = () => (
  <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
    <Box sx={{ bgcolor: V1_ACCENT_TINT, borderRadius: '16px', p: '16px 20px', display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 'none', width: 26, height: 26, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <InfoOutlinedIcon sx={{ color: V1_ACCENT, fontSize: 18 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 650, mb: 0.4 }}>About your allergies</Typography>
        <Typography sx={{ fontSize: 13, color: '#4a4a52', lineHeight: 1.5, maxWidth: 820 }}>
          These are the allergies your providers documented. The same allergy is often recorded several times across
          systems — we fold matching entries into one card and show the count. This page presents your record as
          documented and does not provide medical advice.
        </Typography>
      </Box>
    </Box>
  </Container>
);

// --- Detail dialog (merged shape — reads events for up to 3 variant names) -------
const AllergyDetailDialog: FC<{ allergy: MergedAllergy | null; onClose: () => void }> = ({ allergy, onClose }) => {
  const names = (allergy?.variants ?? []).map((v) => v.name).slice(0, 3);
  const q0 = useGetResourceEventsQuery({ resource: 'allergies', name: names[0] ?? '' }, { skip: !allergy || !names[0] });
  const q1 = useGetResourceEventsQuery({ resource: 'allergies', name: names[1] ?? '' }, { skip: !allergy || !names[1] });
  const q2 = useGetResourceEventsQuery({ resource: 'allergies', name: names[2] ?? '' }, { skip: !allergy || !names[2] });
  if (!allergy) return null;
  const chip = statusChip({ status: allergy.status } as GroupedSummary);
  const loading = q0.isFetching || q1.isFetching || q2.isFetching;
  const isError = q0.isError || q1.isError || q2.isError;
  const refetch = () => { if (names[0]) q0.refetch(); if (names[1]) q1.refetch(); if (names[2]) q2.refetch(); };
  const events = [...(q0.data ?? []), ...(q1.data ?? []), ...(q2.data ?? [])]
    .sort((a, b) => (b.start ?? '').localeCompare(a.start ?? ''));
  const aka = allergy.variants.map((v) => v.name).filter((n) => n !== allergy.name);
  const fact = (label: string, value: string, bordered = false) => (
    <Box sx={{ flex: 1, minWidth: 120, ...(bordered ? { borderLeft: '1px solid #f0f0f3', pl: 2 } : {}) }}>
      <Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 0.5 }}>{value}</Typography>
    </Box>
  );
  return (
    <Dialog open={!!allergy} onClose={onClose} maxWidth="md" fullWidth transitionDuration={0}
      PaperProps={{ sx: { borderRadius: '22px', bgcolor: '#f7f5fb', overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: 'white', p: '24px 26px', borderBottom: '1px solid #ece9f4', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 25, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.15 }}>{allergy.name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
            {chip ? (
              <Box sx={{ fontSize: '12.5px', fontWeight: 700, px: 1.5, py: 0.6, borderRadius: '20px', bgcolor: chip.bg, color: chip.fg }}>{chip.label}</Box>
            ) : null}
            <Typography sx={{ fontSize: '12.5px', color: '#8e8e93' }}>
              {catMeta(mergedCategory(allergy)).cardLabel} · as documented in your health record
            </Typography>
          </Box>
          {aka.length > 0 && (
            <Typography sx={{ fontSize: 12, color: '#8e8e93', mt: 0.75 }}>Also recorded as: {aka.join(', ')}</Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ bgcolor: '#ececed', color: '#6b6b70' }}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ p: '22px !important' }}>
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: 'white' }}>
          {/* Same calendar day for first & last → show one "Date recorded" (formatDate is day-level). */}
          {allergy.firstDate && formatDate(allergy.firstDate) === formatDate(allergy.lastDate) ? (
            fact('Date recorded', formatDate(allergy.firstDate))
          ) : (
            <>
              {fact('First documented', allergy.firstDate ? formatDate(allergy.firstDate) : '—')}
              {fact('Most recent', allergy.lastDate ? formatDate(allergy.lastDate) : '—', true)}
            </>
          )}
          {fact('Records', allergy.count.toLocaleString('en-US'), true)}
          {fact('Reaction', allergy.reaction || 'Not documented', true)}
        </Paper>

        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px 8px', bgcolor: 'white' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#6b6b70', textTransform: 'uppercase', mb: 2 }}>Documentation history</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Box>
          ) : isError ? (
            <DocHistoryError onRetry={refetch} />
          ) : (
            // Shared EventTimeline (same as every other dialog) — it takes any event list, so the
            // multi-variant concatenation feeds it directly.
            <EventTimeline eventList={events} chartData={null} resource="allergies" groupName={allergy.name} growWithContent />
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

// --- 3. Body: search + category pills + sections + admin footnote ----------------
type FilterKey = 'all' | AllergyCategoryKey;

export const AllergiesBody: FC<SectionProps> = () => {
  const { merged, admin, isLoading } = useAllergyGroups();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<MergedAllergy | null>(null);

  const q = search.trim().toLowerCase();
  const matches = (a: MergedAllergy) => !q || a.name.toLowerCase().includes(q);
  if (isLoading) return null; // hero shows the spinner

  const shown = merged.filter(matches);
  const bySection: Record<AllergyCategoryKey, MergedAllergy[]> = { medication: [], food: [], other: [] };
  shown.forEach((a) => { bySection[mergedCategory(a)].push(a); });

  const pill = (key: FilterKey, label: string) => {
    // Hide a classifier pill when its category has nothing to show (full data, ignoring the search box).
    if (key !== 'all' && !merged.some((a) => mergedCategory(a) === key)) return null;
    return (
    <Box key={key} onClick={() => setFilter(key)} sx={{
      fontSize: 13, fontWeight: 600, px: 2, py: 1, borderRadius: '20px', cursor: 'pointer',
      bgcolor: filter === key ? V1_ACCENT : 'white', color: filter === key ? 'white' : '#3a3a3c',
      border: `1px solid ${filter === key ? V1_ACCENT : '#e0e0e6'}`,
    }}>{label}</Box>
    );
  };

  const card = (a: MergedAllergy) => {
    const chip = statusChip({ status: a.status } as GroupedSummary);
    return (
      <Box key={a.name} onClick={() => setSelected(a)} sx={{
        bgcolor: '#fdfcff', border: '1px solid #ece9f4', borderRadius: '14px', p: 2.25, cursor: 'pointer',
        '&:hover': { borderColor: V1_ACCENT_BORDER, bgcolor: 'white' },
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.25 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 650, letterSpacing: '-0.2px', lineHeight: 1.25 }}>{a.name}</Typography>
          {chip ? (
            <Box sx={{ flex: 'none', fontSize: '10.5px', fontWeight: 700, px: 1.25, py: 0.5, borderRadius: '20px', bgcolor: chip.bg, color: chip.fg, whiteSpace: 'nowrap' }}>{chip.label}</Box>
          ) : null}
        </Box>
        <Typography sx={{ fontSize: 12, color: '#6b6b70', mt: 0.5 }}>
          {catMeta(mergedCategory(a)).cardLabel}{a.reaction ? ` · Reaction: ${a.reaction}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, pt: 1.25, borderTop: '1px solid #f0eef6' }}>
          <Typography sx={{ fontSize: 12, color: '#6b6b70' }}>
            {a.count} record{a.count === 1 ? '' : 's'}{a.lastDate ? ` · last ${formatDate(a.lastDate)}` : ''}
          </Typography>
          <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: V1_ACCENT, whiteSpace: 'nowrap' }}>View details ›</Typography>
        </Box>
      </Box>
    );
  };

  const section = (key: AllergyCategoryKey, title: string, dot: string, emptyLabel: string) => {
    if (filter !== 'all' && filter !== key) return null;
    const list = bySection[key];
    if (filter === 'all' && list.length === 0 && key !== 'medication') return null; // design: hide empty non-primary sections
    return (
      <Paper key={key} elevation={0} sx={{ borderRadius: '18px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '18px 22px' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dot, flex: 'none' }} />
          <Typography component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', flex: 1 }}>{title}</Typography>
          <Box sx={{ fontSize: '12.5px', fontWeight: 700, color: '#6b6b70', bgcolor: '#f0f0f3', px: 1.4, py: 0.5, borderRadius: '20px' }}>{list.length}</Box>
        </Box>
        <Box sx={{ px: 2.75, pb: 2.75 }}>
          {list.length ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 1.75 }}>
              {list.map(card)}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13, color: '#8e8e93' }}>{emptyLabel}</Typography>
          )}
        </Box>
      </Paper>
    );
  };

  const adminNote = admin.length
    ? `Your record also contains ${admin.map((g) => `${g.count} “${g.name}”`).join(' and ')} ${admin.length === 1 ? 'entry' : 'entries'} — these describe how the record was documented and are not allergies themselves.`
    : null;

  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2.5, pb: 12 }}>
      <V1SearchField value={search} onChange={setSearch} placeholder="Search allergies" />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {pill('all', 'All')}
        {ALLERGY_CATS.map((c) => pill(c.key, c.pill))}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {ALLERGY_CATS.map((c) => section(c.key, c.title, c.dot, c.empty))}
      </Box>
      {q && shown.length === 0 && (
        <Typography sx={{ p: '40px 26px', textAlign: 'center', color: '#8e8e93', fontSize: 15 }}>
          No allergies match &ldquo;{search}&rdquo;.
        </Typography>
      )}
      {adminNote && (
        <Box sx={{ mt: 2.5, p: '14px 20px', bgcolor: '#fbfbfd', border: '1px solid #ececf1', borderRadius: '14px' }}>
          <Typography sx={{ fontSize: 12.5, color: '#6b6b70', lineHeight: 1.55 }}>{adminNote}</Typography>
        </Box>
      )}
      <Box sx={{ mt: 4.5, pt: 2.75, borderTop: '1px solid #e2e2e8', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 12, color: '#aeaeb2', lineHeight: 1.55, maxWidth: 660, mx: 'auto' }}>
          This page lists the allergies documented by your healthcare providers. It presents your health record as
          documented and does not provide medical advice.
        </Typography>
      </Box>
      <AllergyDetailDialog allergy={selected} onClose={() => setSelected(null)} />
    </Container>
  );
};
